import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { X, Search, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export function MedicalDiagnosisSelect({ value = [], onChange, disabled = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/diagnosis-codes?q=${encodeURIComponent(query)}`);
        setResults(data.filter(d => !value.some(v => v.code === d.code)));
      } catch {} finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addCode = (code) => {
    onChange([...value, { code: code.code, description: code.description, source: code.source }]);
    setQuery(''); setResults([]); setOpen(false);
  };

  const removeCode = (code) => { onChange(value.filter(v => v.code !== code)); };

  return (
    <div ref={ref} className="relative" data-testid="medical-diagnosis-select">
      <label className="text-xs font-medium text-[#1A3330] mb-1 block">Medical Diagnosis (ICD-11 / DSM-5-TR)</label>
      {/* Selected pills */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {value.map(v => (
            <Badge key={v.code} className="bg-blue-100 text-blue-700 border-0 text-[10px] font-normal flex items-center gap-1 pr-1" data-testid={`med-pill-${v.code}`}>
              <span className="font-semibold">{v.code}</span> {v.description?.substring(0, 30)}{v.description?.length > 30 ? '...' : ''}
              {!disabled && <button type="button" onClick={() => removeCode(v.code)} className="ml-0.5 hover:text-blue-900"><X className="w-3 h-3" /></button>}
            </Badge>
          ))}
        </div>
      )}
      {/* Search input */}
      {!disabled && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#4A6B68]" />
          <input
            value={query} onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder='Search codes or keywords (e.g. "depression", "F32")...'
            className="w-full h-9 pl-8 pr-3 rounded-xl border border-[#E5F2F1] text-sm text-[#1A3330] placeholder:text-[#4A6B68]/60 focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
            data-testid="medical-diagnosis-search"
          />
          {/* Dropdown */}
          {open && (query.length >= 2 || results.length > 0) && (
            <div className="absolute z-50 top-full mt-1 w-full bg-white rounded-xl border border-[#E5F2F1] shadow-lg max-h-48 overflow-y-auto">
              {loading && <div className="px-3 py-2 text-xs text-[#4A6B68]">Searching...</div>}
              {!loading && results.length === 0 && query.length >= 2 && <div className="px-3 py-2 text-xs text-[#4A6B68]">No matching codes found</div>}
              {results.map(r => (
                <button type="button" key={r.code} onClick={() => addCode(r)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex items-center gap-2 border-b border-[#E5F2F1] last:border-0"
                  data-testid={`code-option-${r.code}`}>
                  <span className="font-semibold text-blue-600 text-xs flex-shrink-0">{r.code}</span>
                  <span className="text-[#1A3330] text-xs truncate">{r.description}</span>
                  <span className="ml-auto text-[9px] text-[#4A6B68] flex-shrink-0">{r.source}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const BVIP_OPTIONS = ['Mudha', 'Kshipta', 'Vikshipta', 'Ekagra'];

export function BvipDiagnosisSelect({ value = [], onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt));
    else onChange([...value, opt]);
  };

  return (
    <div ref={ref} className="relative" data-testid="bvip-diagnosis-select">
      <label className="text-xs font-medium text-[#1A3330] mb-1 block">BVIP Diagnosis (Brhdhi Vidya)</label>
      {/* Selected pills */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {value.map(v => (
            <Badge key={v} className="bg-purple-100 text-purple-700 border-0 text-[10px] font-normal flex items-center gap-1 pr-1" data-testid={`bvip-pill-${v}`}>
              {v}
              {!disabled && <button type="button" onClick={() => toggle(v)} className="ml-0.5 hover:text-purple-900"><X className="w-3 h-3" /></button>}
            </Badge>
          ))}
        </div>
      )}
      {/* Dropdown */}
      {!disabled && (
        <div className="relative">
          <button type="button" onClick={() => setOpen(!open)}
            className="w-full h-9 px-3 rounded-xl border border-[#E5F2F1] text-sm text-left flex items-center justify-between text-[#1A3330] hover:border-purple-300 focus:ring-1 focus:ring-purple-400"
            data-testid="bvip-dropdown-trigger">
            <span className={value.length === 0 ? 'text-[#4A6B68]/60' : ''}>{value.length === 0 ? 'Select BVIP diagnosis...' : `${value.length} selected`}</span>
            <ChevronDown className="w-4 h-4 text-[#4A6B68]" />
          </button>
          {open && (
            <div className="absolute z-50 top-full mt-1 w-full bg-white rounded-xl border border-[#E5F2F1] shadow-lg">
              {BVIP_OPTIONS.map(opt => (
                <button type="button" key={opt} onClick={() => toggle(opt)}
                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 border-b border-[#E5F2F1] last:border-0 ${value.includes(opt) ? 'bg-purple-50 text-purple-700' : 'hover:bg-[#F4FAF9] text-[#1A3330]'}`}
                  data-testid={`bvip-option-${opt.toLowerCase()}`}>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${value.includes(opt) ? 'border-purple-500 bg-purple-500' : 'border-[#E5F2F1]'}`}>
                    {value.includes(opt) && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DiagnosisPills({ medicalDiagnosis = [], bvipDiagnosis = [], compact = false }) {
  const md = medicalDiagnosis || [];
  const bv = bvipDiagnosis || [];
  if (md.length === 0 && bv.length === 0) return null;
  return (
    <div className={compact ? 'space-y-0.5' : 'space-y-1'}>
      {md.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {md.slice(0, compact ? 3 : 10).map(d => (
            <Badge key={d.code} className="bg-blue-100 text-blue-700 border-0 text-[10px] font-normal">{d.code}</Badge>
          ))}
          {compact && md.length > 3 && <span className="text-[10px] text-[#4A6B68]">+{md.length - 3}</span>}
        </div>
      )}
      {bv.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {bv.map(b => (
            <Badge key={b} className="bg-purple-100 text-purple-700 border-0 text-[10px] font-normal">{b}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}
